<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import useRenditionStore from '../../stores/rendition';
import useReaderStore from '../../stores/reader';
import { useDisplay, useTheme } from 'vuetify';
import useNavigationStore from '../../stores/navigation';
import useNavigationTriggerStore from '../../stores/navigation-trigger';
import type { GoToItem } from '../../types';

type Emits = {
  (e: 'goToContent', item: GoToItem): void;
};
const emit = defineEmits<Emits>();

const { viewMode, isScrolledBook } = storeToRefs(useReaderStore());
const { isRTL, isReflowable } = storeToRefs(useRenditionStore());
const { currentSpineIndex, currentPositionPct, totalSpineItems } = storeToRefs(useNavigationStore());
const { getSectionByTarget, getCFIFromPercentage } = useRenditionStore();
const { pages, currentPageIndex, hasPages, isInitializing, resolutionProgress } = storeToRefs(useNavigationStore());
const { setScrubbing, setNavigating } = useNavigationStore();
const { setNavigationTrigger } = useNavigationTriggerStore();

const { xs } = useDisplay();
const theme = useTheme();

const onBrandColor = computed(() => theme.current.value.colors['on-brand']);

const liveSliderValue = ref(currentPageIndex.value);

const sliderMax = computed(() => {
  if (hasPages.value) return pages.value.length - 1;

  // Fallback Logic
  if (isReflowable.value) return 100; // Percentage mode
  return totalSpineItems.value - 1 || 0; // Fixed-layout mode
});

const sliderTotal = computed(() => {
  if (hasPages.value) return pages.value[pages.value.length - 1]?.page || pages.value.length;

  // Fallback Logic
  if (isReflowable.value) return '100%';
  return totalSpineItems.value;
});

const currentSliderValue = computed(() => {
  if (hasPages.value) return currentPageIndex.value;

  // Fallback Logic
  if (isReflowable.value) return Math.round(currentPositionPct.value);
  return currentPositionPct.value;
});

watch(
  currentSliderValue,
  (newValue) => {
    liveSliderValue.value = newValue;
  },
  { immediate: true },
);

const handleUpdate = (val: number) => {
  liveSliderValue.value = val;
  // Push the real-time value to the store for the distant overlay
  setScrubbing(val);
};

const handleChange = (newIndex: number) => {
  setScrubbing(null);
  setNavigating(true);
  // Only fired on drag end, so one trigger per drag rather than one per slider step.
  setNavigationTrigger('SLIDER');

  if (hasPages.value) {
    const targetPage = pages.value[newIndex];
    if (targetPage) {
      emit('goToContent', {
        target: targetPage.cfi || targetPage.href,
        isGoingForwardsInBook: newIndex > currentPageIndex.value,
      });
    }
    return;
  }

  // Fallback: Reflowable Percentage
  if (isReflowable.value) {
    // Get CFI from percentage via Epub.js locations
    const target = getCFIFromPercentage(newIndex);
    if (target) {
      emit('goToContent', { target, isGoingForwardsInBook: newIndex / 100 > currentPositionPct.value });
      return;
    }
  }

  // Fallback: Fixed Layout (Spine Index)
  const spineItem = getSectionByTarget(newIndex);
  if (spineItem) {
    emit('goToContent', { target: spineItem.href, isGoingForwardsInBook: newIndex > currentSpineIndex.value });
  }
};

const progressWrapperRef = ref<HTMLElement | null>(null);
onMounted(() => {
  liveSliderValue.value = currentSliderValue.value;
});
</script>

<template>
  <div
    v-if="!isScrolledBook"
    ref="progressWrapperRef"
    class="progress-wrapper d-flex flex-column justify-center w-100"
    data-testid="bp-progress-slider"
  >
    <div class="d-flex align-center justify-space-between w-100" :class="{ 'mt-2': xs }">
      <!-- Prepend Slot -->
      <div class="d-flex align-center">
        <slot name="prepend"></slot>
      </div>

      <!-- Main Progress / Slider Content -->
      <div class="d-flex align-center flex-grow-1 justify-center px-2">
        <v-locale-provider :rtl="isRTL" class="bg-brand">
          <v-progress-linear
            v-if="isInitializing"
            :model-value="resolutionProgress"
            :color="onBrandColor"
            height="6"
            rounded
            class="mx-6"
          ></v-progress-linear>
          <v-slider
            v-else
            :model-value="liveSliderValue"
            class="progress-slider bg-brand w-100"
            :min="0"
            :max="sliderMax"
            hide-details
            :color="onBrandColor"
            step="1"
            @end="handleChange"
            @update:model-value="handleUpdate"
          >
            <template v-if="viewMode === 'paginated'" #prepend>
              <p class="text-body-1 mt-1 hidden-xs text-on-brand"></p>
            </template>
            <template #append>
              <p class="text-body-1 mt-1 text-on-brand hidden-xs">
                {{ sliderTotal }}
              </p>
            </template>
          </v-slider>
        </v-locale-provider>
      </div>
      <!-- Append Slot -->
      <div class="d-flex align-center">
        <slot name="append" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.progress-wrapper {
  min-height: 48px;
}

:deep(.v-slider) {
  cursor: grab;
}

:deep(.v-slider-thumb__label) {
  top: -20px !important;
  background-color: transparent !important;
  color: rgb(var(--v-theme-on-brand)) !important;
}

:deep(.v-slider-thumb__surface) {
  z-index: 100 !important;
  position: relative !important;
}

:deep(.v-slider-thumb__label::before) {
  color: transparent;
}

:deep(.v-slider__thumb-container .v-slider__thumb-label) {
  height: 12px !important;
  background-color: transparent !important;
  border-color: transparent !important;

  .thumb-label-text {
    font-weight: bold;
    padding-top: 12px;
  }
}
// Override from node_modules/vuetify/src/components/VSlider/variables.sass
$slider-thumb-focused-size-increase: 30px;
$slider-thumb-size: 20px;

:deep(.v-slider__thumb) {
  width: $slider-thumb-size;
  height: $slider-thumb-size;
  left: calc($slider-thumb-focused-size-increase / 2);

  &:before {
    width: #{$slider-thumb-size + $slider-thumb-focused-size-increase};
    height: #{$slider-thumb-size + $slider-thumb-focused-size-increase};
    left: calc($slider-thumb-focused-size-increase / 2);
    top: calc($slider-thumb-focused-size-increase / 2);
  }
}

:deep(.v-slider--disabled .v-slider__thumb) {
  width: #{$slider-thumb-size - 4};
  height: #{$slider-thumb-size - 4};
  left: calc(#{$slider-thumb-size - 4} / 2);
}
</style>
